import { Button, Col, Divider, Flex, Form, Input, InputNumber, Modal, Popconfirm, Row, Select, Space, Switch, Table, Tabs, message } from 'antd';
import { PageHeader } from "@ant-design/pro-layout";
import { DeleteFilled, MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { ColumnsType } from 'antd/lib/table';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { marketplaces } from 'helpers';
import { updateProductSelector, deleteProductSelector, getProductSelectorList, getProductSelectorListBySupplier, getSupplierList } from 'services';
import { Helmet } from 'react-helmet';
import { TSupplier } from './ProductListURLs';
import TextArea from 'antd/es/input/TextArea';

type TSelectorState = {
  _id?: string,
  supplier: string,
  titles: string[],
  prices: string[],
  codes: string[], //same as an ID
  images: string[],
  quantities: string[],
  productUrls: string[],
  productSections: string[],
  pricesCategory: string[],
  paginationParams: string[],
  isActive?: boolean,
  isActiveAZInsight?: boolean,
  note?: string | null,
}

type TFormLabel = {
  label: string,
  child?: { [key: string]: string }
}

const initialProductSeletor = {
  supplier: '',
  titles: [""],
  prices: [],
  codes: [""],
  images: [""],
  quantities: [""],
  productUrls: [""],
  productSections: [""],
  pricesCategory: [],
  paginationParams: [],
  isActive: false,
  isActiveAZInsight: false,
  note: null
}

const formLabels: { [key: string]: TFormLabel } = {
  titles: {
    label: "Title",
  },
  productUrls: {
    label: "Product URLs",
  },
  prices: {
    label: "Price",
    child: {
      int: "text",
      dec: "text",
    },
  },
  productSections: {
    label: "Sections of Product (Cateogry)",
  },
  pricesCategory: {
    label: "Product Prices (Category)",
    child: {
      int: "text",
      dec: "text",
    },
  },
  codes: {
    label: "Barcode",
  },
  images: {
    label: "Image",
  },
  quantities: {
    label: "Quantity",
  },
  note: {
    label: "Note"
  },
  isActive: {
    label: "isActiveAddon"
  },
  isActiveAZInsight: {
    label: "isActiveAZInsight"
  },
  paginationParams: {
    label: "Pagination Params",
    child: {
      key: "text",
      value: "int",
    },
  },
}

const ProductSelectors = () => {
  const [form] = Form.useForm();
  const [activedTab, setActivedTab] = useState<string>("US")
  const [dataTable, setDataTable] = useState<any>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [loadingTable, setLoadingTable] = useState<boolean>(false)
  const [openNoteModal, setOpenNoteModal] = useState<boolean>(false)
  const [suppliers, setSuppliers] = useState<any[]>([])
  const [selectedSupplierDropdown, setSelectedSupplierDropdown] = useState<string>("")
  const [selectedSupplier, setSelectedSupplier] = useState<TSelectorState | undefined>()
  const [updateSelector, setUpdateSelector] = useState<TSelectorState>(initialProductSeletor)

  let { marketplace = "US" } = useParams();
  let navigate = useNavigate();

  const handleOnChangeTab = (tabKey: string) => {
    navigate(`../${tabKey}`, { replace: true });
  };

  const handleOnSelectSupplier = (sl: string) => {
    setSelectedSupplierDropdown(sl)
    handleOnGetSelectorsBySupplier(sl)
  }

  const handleOnDelete = (item: any) => {
    setLoading(true)
    const id = item._id
    return deleteProductSelector(id, marketplace as string)
      .then(() => {
        const newData = dataTable.filter((d: any) => d._id !== id);
        setDataTable(newData);
        message.success("Deleted a record successfully")
      })
      .catch(() => message.error("Cannot delete record"))
      .finally(() => {
        setLoading(false)
        if (selectedSupplierDropdown === item.supplier) {
          form.setFieldsValue(initialProductSeletor)
          setUpdateSelector(initialProductSeletor)
        }
      })
  }

  const handleOnUpdateSelector = () => {
    const payload = updateSelector
    updateSelectors(payload)
  };

  const handleOnGetSelectorsBySupplier = (supplier: string) => {
    setLoadingTable(true);
    return getProductSelectorListBySupplier(marketplace, supplier)
      .then(({ data }: any) => {
        if (data) {
          form.setFieldsValue(data);
          setUpdateSelector((prev) => ({ ...prev, _id: data._id }))
        } else {
          form.setFieldsValue(initialProductSeletor)
        }
      })
      .catch(() => {
        message.error('Failed get selectors by supplier')
      })
      .finally(() => setLoadingTable(false))
  }

  const handleOnChangeFields = (_: any, allFields: any) => {
    setUpdateSelector((prevState: TSelectorState) => {
      return ({
        _id: prevState._id || '',
        supplier: selectedSupplierDropdown,
        titles: allFields.titles,
        prices: allFields.prices,
        codes: allFields.codes,
        images: allFields.images,
        quantities: allFields.quantities,
        productUrls: allFields.productUrls,
        pricesCategory: allFields.pricesCategory,
        productSections: allFields.productSections,
        paginationParams: allFields.paginationParams,
        isActive: allFields.isActive,
        isActiveAZInsight: allFields.isActiveAZInsight,
        note: allFields.note
      })
    });
  };

  const handleOnClickSupplier = (item: TSelectorState) => {
    setLoadingTable(true);
    form.setFieldsValue(item);
    setUpdateSelector((prev) => {
      setSelectedSupplierDropdown(item.supplier)
      setLoadingTable(false);
      return ({ ...prev, _id: item._id })
    })
  }

  const handleOnActive = (field: any, value: boolean, item: TSelectorState) => {
    const payload = {
      ...item,
      [field]: !value,
    }
    updateSelectors(payload)
    if (payload?.supplier === selectedSupplierDropdown) {
      handleOnClickSupplier(payload)
    }
  }

  const handleOnClickEditNote = (event: React.MouseEvent, supplier: TSelectorState) => {
    setOpenNoteModal(true);
    setSelectedSupplier(supplier)
  }

  const handleOnUpdateNote = () => {
    const payload = selectedSupplier as TSelectorState
    updateSelectors(payload)
    if (selectedSupplier?.supplier === selectedSupplierDropdown) {
      handleOnClickSupplier(payload)
    }
  }

  useEffect(() => {
    setLoading(true)
    getSupplierList(marketplace)
      .then(({ data }) => {
        const optList = data.data.map((spl: TSupplier) => ({
          label: spl.domain,
          value: spl.domain
        }))
        handleOnSelectSupplier(optList[0].value)
        setSuppliers(_ => {
          return optList
        })
      })
      .catch(() =>
        message.error("Cannot get supplier list, please refresh page again")
      )
      .finally(() => setLoading(false))
  }, [marketplace])

  useEffect(() => {
    setLoading(true)
    getProductSelectorList(marketplace)
      .then(({ data }) => setDataTable(data.data))
      .catch(() =>
        message.error("Cannot get product selector list, please refresh page again")
      )
      .finally(() => setLoading(false))
  }, [activedTab, marketplace])

  useEffect(() => {
    setActivedTab(marketplace || "US")
  }, [marketplace]);

  const updateSelectors = (payload: TSelectorState) => {
    setLoadingTable(true);
    return updateProductSelector(marketplace as string, payload)
      .then(({ data }: any) => {
        setDataTable((prev: any) => {
          const existingItem = prev.filter((item: any) => item.supplier === data.supplier)
          if (existingItem.length > 0) {
            return prev.map((item: any) => item.supplier === data.supplier ? data : item)
          }
          const newRow = {
            ...payload,
            _id: data._id,
          };
          return [newRow].concat(prev)
        });
        message.success('Update selector successfully');
      })
      .catch(() => {
        message.error('Failed updating new selector')
      })
      .finally(() => setLoadingTable(false))
  }

  const columns: ColumnsType<any> = [
    {
      title: 'Supplier',
      dataIndex: 'supplier',
      key: 'supplier',
      fixed: 'left',
      defaultSortOrder: 'descend',
      sorter: (a, b) => a.supplier.localeCompare(b.supplier),
    },
    {
      title: 'isActiveAddon',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (value, record) => {
        const active = value === true ? "Yes" : "No"
        return (
          <Space>
            <strong>{active}</strong>
            <Popconfirm
              placement="topRight"
              title={`Are you sure to ${record.isActive ? "Deactive" : "Activate"} this supplier for Addon?`}
              onConfirm={() => handleOnActive('isActive', value, record)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="primary" danger={record.isActive}>
                {record.isActive ? "Deactive" : "Activate"}
              </Button>
            </Popconfirm>
          </Space>
        )
      }
    },
    {
      title: 'isActiveAZInsight',
      dataIndex: 'isActiveAZInsight',
      key: 'isActiveAZInsight',
      render: (value, record) => {
        const active = value === true ? "Yes" : "No"
        return (
          <Space>
            <strong>{active}</strong>
            <Popconfirm
              placement="topRight"
              title={`Are you sure to ${record.isActiveAZInsight ? "Deactive" : "Activate"} this supplier for AZInsight?`}
              onConfirm={() => handleOnActive('isActiveAZInsight', value, record)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="primary" danger={record.isActiveAZInsight}>
                {record.isActiveAZInsight ? "Deactive" : "Activate"}
              </Button>
            </Popconfirm>
          </Space>
        )
      }
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
    },
    {
      title: 'Actions',
      key: 'action',
      fixed: 'right',
      render: (_, record: any) => {
        return (
          <Space size="middle">
            <Button onClick={(e) => handleOnClickEditNote(e, record)}>
              Edit Note
            </Button>
            <Popconfirm
              placement="topRight"
              title="Are you sure to delete this record?"
              onConfirm={() => handleOnDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="primary" icon={<DeleteFilled />} danger />
            </Popconfirm>
          </Space>
        )
      },
    },
  ];

  const renderInput = (value: string = "text") => value === "text" ? <TextArea /> : <InputNumber controls={false} />

  const RenderInputGroupProductSelector = () => {
    return (
      <Form
        name="basic"
        form={form}
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        onValuesChange={handleOnChangeFields}
        initialValues={form.getFieldsValue()}
        autoComplete="off"
      >
        <Space direction="vertical" size={20} wrap style={{ width: "100%" }}>
          {Object.keys(formLabels).map(fk => {
            const objKeys = formLabels[fk].child && Object.keys(formLabels[fk].child!)
            const objValues = formLabels[fk].child && Object.values(formLabels[fk].child!)
            const isSpecial = ["note", "isActive", "isActiveAZInsight"].includes(fk)
            return (
              <Row key={`${fk}-row`} gutter={8}>
                <Col span={2}>
                  <label>{formLabels[fk].label}</label>
                </Col>
                <Col span={10}>
                  {isSpecial ? (
                    fk === "note" ? (
                      <Form.Item name={fk} key={`${fk}`} noStyle>
                        {renderInput()}
                      </Form.Item>
                    ) : (
                      <Form.Item name={fk} key={`${fk}`} noStyle>
                        <Switch />
                      </Form.Item>
                    )) : (
                    <Form.List
                      name={fk}
                    >
                      {(fields, { add, remove }) => {
                        return (
                          <Space direction="vertical" wrap style={{ marginBottom: "10px", width: "100%" }}>
                            {fields.map(({ key, name }) => (
                              <Flex key={`${fk}-${key}`} gap={10} style={{ width: "100%" }}>
                                {objKeys && objValues ? (
                                  <>
                                    <Form.Item
                                      name={[name, objKeys[0]]}
                                      key={`${fk}-${key}-${objKeys[0]}`}
                                      noStyle
                                    >
                                      {renderInput(objValues[0])}
                                    </Form.Item>
                                    <Form.Item
                                      name={[name, objKeys[1]]}
                                      key={`${fk}-${key}-${objKeys[1]}`}
                                      noStyle
                                    >
                                      {renderInput(objValues[1])}
                                    </Form.Item>
                                  </>
                                ) : (
                                  <Form.Item
                                    name={[name]}
                                    key={`${fk}-${key}`}
                                    noStyle
                                  >
                                    {renderInput()}
                                  </Form.Item>
                                )}
                                {fields.length > 1 ? (
                                  <MinusCircleOutlined
                                    className="dynamic-delete-button"
                                    onClick={() => remove(name)}
                                  />
                                ) : null}
                              </Flex>
                            ))}
                            <Space>
                              <Button
                                type="dashed"
                                size="small"
                                block
                                onClick={() => add(
                                  objKeys && objValues ?
                                    { [objKeys[0]]: "", [objKeys[1]]: objValues[1] === "text" ? "" : 1 } :
                                    { [fk]: "" }
                                )}
                                icon={<PlusOutlined />}
                              >
                                Add selector for {formLabels[fk].label}
                              </Button>
                            </Space>
                          </Space>
                        )
                      }}
                    </Form.List>
                  )}
                </Col>
              </Row>
            )
          })}
        </Space>
      </Form>
    )
  }

  const RenderInputDomainURL = (selected: string) => (
    <Row gutter={8}>
      <Col span={2}>
        <label>Supplier:</label>
      </Col>
      <Col span={6}>
        <Select
          showSearch
          defaultActiveFirstOption={false}
          loading={loading}
          value={selected || suppliers[0]}
          options={suppliers}
          onChange={handleOnSelectSupplier}
          style={{ width: 200 }}
        />
      </Col>
    </Row>
  )

  const renderTabItems = () => {
    return (
      marketplaces.map(m => ({
        key: m.key,
        label: m.label,
        children: (
          <Space direction="vertical" size="large" style={{ width: '100%', marginTop: 30 }}>
            {RenderInputDomainURL(selectedSupplierDropdown)}
            <Divider orientation="left">Update Product Selectors</Divider>
            {RenderInputGroupProductSelector()}
            <Row gutter={8}>
              <Col span={3}>
                <Button
                  type="primary"
                  disabled={loadingTable}
                  onClick={() => handleOnUpdateSelector()}
                >
                  Save
                </Button>
              </Col>
            </Row>
            <Table
              columns={columns}
              dataSource={dataTable}
              rowKey={(record) => record._id}
              scroll={{ x: 'calc(700px + 50%)' }}
              bordered
              size="middle"
              onRow={(record) => {
                return {
                  onDoubleClick: (event) => handleOnClickSupplier(record)
                };
              }}
              expandable={{
                expandedRowRender: (record) => <div style={{ overflow: "scroll" }}><pre>{JSON.stringify(record, undefined, 2)}</pre></div>,
              }}
            />
          </Space>
        )
      }))
    )
  }

  return (
    <>
      <Helmet>
        <title>Product Selectors</title>
      </Helmet>
      <PageHeader
        className="page-header"
        backIcon={true}
        title="Product Selectors"
        footer={
          <Tabs
            defaultActiveKey={activedTab}
            type="card"
            activeKey={activedTab}
            onChange={handleOnChangeTab}
            items={renderTabItems()}
          />
        }
      />
      <Modal
        closable
        open={openNoteModal}
        title={`${selectedSupplier?.supplier} - Note`}
        onCancel={() => setOpenNoteModal(false)}
        okButtonProps={{
          style: {
            display: 'none'
          }
        }}
      >
        <Input.TextArea
          value={selectedSupplier?.note || ""}
          onChange={
            (e) => setSelectedSupplier(
              selectedSupplier && { ...selectedSupplier, note: e.target.value }
            )
          }
        />
        <Button onClick={handleOnUpdateNote} type="primary" style={{ marginTop: 5 }}>Update</Button>
      </Modal>
    </>
  )
};

export default ProductSelectors;